import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    DashboardQuery, DashboardResult, DashboardPeriodName,
    getDashboard, getBranches, Branch,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../utils/roles";
import { FaLock, FaSyncAlt, FaPrint, FaChevronRight } from "react-icons/fa";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";

/**
 * What the agency sold.
 *
 * Every figure comes from the same stored number the daily limit counts, so a
 * sale is never valued one way here and another way there.
 *
 * Three things are deliberately kept apart rather than folded into one headline:
 * refunds are not subtracted from sales, money taken but not yet ticketed is not
 * counted as a sale, and sales whose ticketing broke are called out — somebody
 * has to act on those, and a single net figure would hide all three.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

const money = (n: number, currency = "AED") =>
    `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PERIODS: { key: DashboardPeriodName; label: string }[] = [
    { key: "day", label: "Today" },
    { key: "week", label: "This week" },
    { key: "month", label: "This month" },
    { key: "year", label: "This year" },
];

/** The bars in the daily strip, scaled to the busiest day in view. */
const Sparkline: React.FC<{ data: { date: string; sales: number }[]; currency: string }> = ({ data, currency }) => {
    const peak = Math.max(1, ...data.map((d) => d.sales));
    if (data.length === 0) return null;

    return (
        <div className="flex items-end gap-1 h-24 overflow-x-auto">
            {data.map((d) => (
                <div key={d.date} className="flex-1 min-w-[8px] flex flex-col justify-end h-full group relative">
                    <div
                        className={`w-full rounded-t ${d.sales > 0 ? "bg-primary" : "bg-gray-200"}`}
                        style={{ height: `${Math.max(2, (d.sales / peak) * 100)}%` }}
                    />
                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] text-white">
                        {d.date} · {money(d.sales, currency)}
                    </div>
                </div>
            ))}
        </div>
    );
};

const Dashboard: React.FC = () => {
    const { profile } = useAuth();
    const navigate = useNavigate();

    const [query, setQuery] = useState<DashboardQuery>({ period: "week" });
    const [data, setData] = useState<DashboardResult | null>(null);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const allowed = can(profile, "dashboard");

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        try {
            setData(await getDashboard(query));
            setError("");
        } catch (e: any) {
            setError(errorOf(e, "Could not load the figures."));
        } finally {
            setLoading(false);
        }
    }, [allowed, query]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!allowed) return;
        // The branch names, so the filter reads as places rather than ids.
        getBranches().then(setBranches).catch(() => setBranches([]));
    }, [allowed]);

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">
                        Only head office can see the sales figures.
                    </p>
                </div>
            </div>
        );
    }

    const c = data?.currency ?? "AED";
    const t = data?.totals;

    return (
        <div className="max-w-5xl mx-auto px-4 py-8 print:max-w-none print:px-0">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Sales</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {data ? data.period.label : "…"} · Dubai time
                    </p>
                </div>
                <div className="flex gap-2 print:hidden">
                    <button
                        onClick={() => window.print()}
                        disabled={loading || !data}
                        className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FaPrint /> PDF
                    </button>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {/* ── Period and filters ── */}
            <div className="flex flex-wrap items-center gap-2 mb-6 print:hidden">
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            onClick={() => setQuery((q) => ({ ...q, period: p.key, from: undefined, to: undefined }))}
                            className={`px-3 py-2 text-sm ${
                                query.period === p.key
                                    ? "bg-gray-900 text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <select
                    value={query.officeId ?? ""}
                    onChange={(e) => setQuery((q) => ({ ...q, officeId: e.target.value || undefined }))}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                        <option key={b.officeId} value={b.officeId}>{b.name}</option>
                    ))}
                </select>

                <select
                    value={query.airline ?? ""}
                    onChange={(e) => setQuery((q) => ({ ...q, airline: e.target.value || undefined }))}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                    <option value="">All airlines</option>
                    {(data?.airlines ?? []).map((a) => (
                        <option key={a} value={a}>{a}</option>
                    ))}
                </select>

                <div className="flex items-center gap-1">
                    <div className="w-40">
                        <DatePickerWithValidation
                            selected={query.from || undefined}
                            onSelect={(v) => setQuery((q) => ({ ...q, period: "custom", from: v || undefined }))}
                            placeholder="dd/mm/yyyy"
                        />
                    </div>
                    <span className="text-gray-400 text-sm">to</span>
                    <div className="w-40">
                        <DatePickerWithValidation
                            selected={query.to || undefined}
                            onSelect={(v) => setQuery((q) => ({ ...q, period: "custom", to: v || undefined }))}
                            placeholder="dd/mm/yyyy"
                        />
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading && !data && <div className="py-16 text-center text-gray-400">Loading…</div>}

            {t && (
                <>
                    {/* ── The headline ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <div className="text-xs text-gray-500">Sales</div>
                            <div className="mt-1 text-2xl font-semibold text-gray-900">{money(t.sales, c)}</div>
                            <div className="mt-1 text-xs text-gray-400">
                                {t.bookings} booking{t.bookings === 1 ? "" : "s"} · {t.passengers} passenger{t.passengers === 1 ? "" : "s"}
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <div className="text-xs text-gray-500">Refunded</div>
                            <div className="mt-1 text-2xl font-semibold text-gray-900">{money(t.refunded, c)}</div>
                            <div className="mt-1 text-xs text-gray-400">
                                {t.refundedBookings} booking{t.refundedBookings === 1 ? "" : "s"} · not taken off sales
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <div className="text-xs text-gray-500">Awaiting payment</div>
                            <div className="mt-1 text-2xl font-semibold text-gray-900">{money(t.pending, c)}</div>
                            <div className="mt-1 text-xs text-gray-400">
                                {t.pendingBookings} booking{t.pendingBookings === 1 ? "" : "s"} · not a sale yet
                            </div>
                        </div>

                        <div className={`rounded-xl border p-4 ${
                            t.failedBookings > 0 ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"
                        }`}>
                            <div className={`text-xs ${t.failedBookings > 0 ? "text-red-700" : "text-gray-500"}`}>
                                Paid, ticketing failed
                            </div>
                            <div className={`mt-1 text-2xl font-semibold ${
                                t.failedBookings > 0 ? "text-red-700" : "text-gray-900"
                            }`}>
                                {t.failedBookings}
                            </div>
                            <div className={`mt-1 text-xs ${t.failedBookings > 0 ? "text-red-600" : "text-gray-400"}`}>
                                {t.failedBookings > 0
                                    ? `${money(t.failed, c)} taken · not counted as sales`
                                    : "Nothing outstanding"}
                            </div>
                        </div>
                    </div>

                    {/* ── Day by day ── */}
                    {data!.daily.length > 1 && (
                        <div className="rounded-xl border border-gray-200 bg-white p-4 mb-6">
                            <div className="text-xs text-gray-500 mb-3">Day by day</div>
                            <Sparkline data={data!.daily} currency={c} />
                            <div className="mt-2 flex justify-between text-[10px] text-gray-400">
                                <span>{data!.daily[0].date}</span>
                                <span>{data!.daily[data!.daily.length - 1].date}</span>
                            </div>
                        </div>
                    )}

                    {/* ── Branches ── */}
                    <div className="rounded-xl border border-gray-200 bg-white mb-6 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">
                            By branch
                        </div>
                        {data!.byBranch.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-400 text-sm">No sales in this period.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-gray-500 bg-gray-50">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-medium">Branch</th>
                                            <th className="text-right px-4 py-2 font-medium">Sales</th>
                                            <th className="text-right px-4 py-2 font-medium">Bookings</th>
                                            <th className="text-right px-4 py-2 font-medium">Refunded</th>
                                            <th className="text-right px-4 py-2 font-medium">Pending</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {data!.byBranch.map((b) => (
                                            <tr key={b.key}>
                                                <td className="px-4 py-2.5 text-gray-900">{b.label ?? b.key}</td>
                                                <td className="px-4 py-2.5 text-right font-medium">{money(b.sales, c)}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">{b.bookings}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">{money(b.refunded, c)}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">{money(b.pending, c)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── People ── */}
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">
                            By staff member
                        </div>
                        {data!.byStaff.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-400 text-sm">No sales in this period.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-gray-500 bg-gray-50">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-medium">Who</th>
                                            <th className="text-right px-4 py-2 font-medium">Sales</th>
                                            <th className="text-right px-4 py-2 font-medium">Bookings</th>
                                            <th className="text-right px-4 py-2 font-medium">Refunded</th>
                                            <th className="px-4 py-2 print:hidden" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {data!.byStaff.map((s) => (
                                            <tr
                                                key={s.key}
                                                onClick={() => navigate(`/admin/dashboard/staff/${s.key}`)}
                                                className="cursor-pointer hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-2.5">
                                                    <div className="text-gray-900">{s.label ?? s.email ?? s.key}</div>
                                                    {s.label && s.email && (
                                                        <div className="text-xs text-gray-400">{s.email}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-medium">{money(s.sales, c)}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">{s.bookings}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">{money(s.refunded, c)}</td>
                                                <td className="px-4 py-2.5 text-right text-gray-300 print:hidden">
                                                    <FaChevronRight className="inline" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <p className="mt-4 text-xs text-gray-500">
                        Our own records, not Sabre's — the two will not match, which is expected.
                        Website bookings only; WhatsApp and IT's own bookings are left out. Refunds
                        are shown separately and never taken off the sales figure.
                    </p>
                </>
            )}
        </div>
    );
};

export default Dashboard;
